import React, { useEffect, useMemo, useState } from "react";
import {
  FormComponentChange,
  FormErrorType,
  useTestCaseFormContext,
} from "./ValidationTestCaseFormContext";
import { useModal } from "../../../../security-toolbox-ui/StbModalContext";
import StbButton from "../../../../security-toolbox-ui/StbButton";
import {
  HbrInput,
  HbrTextarea,
  HbrTag,
  HbrSelect,
  HbrOption,
} from "@harbor/elements-react-standalone";
import { useParams } from "react-router-dom";
import {
  useFormAddPovTestCaseMutation,
  useFormDeletePovTestCaseMutation,
  useFormUpdatePovTestCaseMutation,
} from "../../../../../hooks/opportunity-pov-hooks";
import { TestCase } from "../../../../../types/opportunity-pov-items";
import { asSingleValue, unicodeToPlain } from "../../../../../support/utils";
import StbModal from "../../../../security-toolbox-ui/StbModal";

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 1000;
const OTHER_OPTION_VALUE = "__other__";

type ProductFeatureSuccessCriteriaOption = {
  id: number;
  name?: string;
  title?: string;
  description?: string;
  success_criteria?: string;
  test_plan?: string;
};

type ProductFeatureOption = {
  id: number;
  name?: string;
  title?: string;
  description?: string;
  product_feature_success_criteria?: ProductFeatureSuccessCriteriaOption[];
};

type ProductUseCaseOption = {
  id: number;
  name?: string;
  title?: string;
  description?: string;
  product_feature?: ProductFeatureOption[];
};

type CriteriaOption = {
  id: number;
  label: string;
  title: string;
  description: string;
  feature_name: string;
  feature_id: number;
};

export const FormSectionTestCase: React.FC<{
  formSectionId: string;
}> = ({ formSectionId }) => {
  const { id: opportunityId } = useParams();
  const { openModal, closeModal } = useModal();
  const {
    getFormItem,
    setFormItemSaved,
    updateFormItem,
    removeFormItem,
    setFormItem,
    setFormError,
    opportunityProduct,
  } = useTestCaseFormContext();

  const testcase = useMemo(() => getFormItem(formSectionId), [getFormItem, formSectionId]);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isAutoSaving, setIsAutoSaving] = useState<boolean>(false);

  const parentUseCase = useMemo(() => {
    return testcase?.parent ? getFormItem(testcase.parent) : undefined;
  }, [getFormItem, testcase?.parent]);

  const productWithCatalog = opportunityProduct?.product as any;

  const productUseCases: ProductUseCaseOption[] = useMemo(() => {
    return (
      productWithCatalog?.use_case ||
      productWithCatalog?.product_use_case ||
      productWithCatalog?.product_use_cases ||
      []
    );
  }, [productWithCatalog]);

  const selectedParentCatalogUseCase = useMemo(() => {
    const selectedUseCaseId = parentUseCase?.data?.product_use_case;
    if (!selectedUseCaseId) return undefined;
    return productUseCases.find((item) => item.id === selectedUseCaseId);
  }, [parentUseCase?.data?.product_use_case, productUseCases]);

  const siblingAssignedCriteriaIds = useMemo(() => {
    const siblingIds = parentUseCase?.children || [];
    return new Set(
      siblingIds
        .filter((id) => id !== formSectionId)
        .map((id) => getFormItem(id)?.data?.product_feature_success_criteria)
        .filter(Boolean)
    );
  }, [parentUseCase?.children, formSectionId, getFormItem]);

  const currentSelectedId = testcase?.data?.product_feature_success_criteria;

  const availableCriteriaOptions: CriteriaOption[] = useMemo(() => {
    if (!selectedParentCatalogUseCase?.product_feature?.length) return [];

    return selectedParentCatalogUseCase.product_feature.flatMap((feature) => {
      const featureName = feature.name || feature.title || "Feature";
      const criteria = feature.product_feature_success_criteria || [];

      return criteria
        .filter((criterion) => {
          if (criterion.id === currentSelectedId) return true;
          return !siblingAssignedCriteriaIds.has(criterion.id);
        })
        .map((criterion) => {
          const criteriaText =
            criterion.success_criteria || criterion.title || criterion.name || "";

          return {
            id: criterion.id,
            label: `${featureName} — ${criteriaText}`,
            title: criteriaText,
            description: criterion.test_plan || criterion.description || "",
            feature_name: featureName,
            feature_id: feature.id,
          };
        });
    });
  }, [selectedParentCatalogUseCase, siblingAssignedCriteriaIds, currentSelectedId]);

  const selectedCriteriaOption = useMemo(() => {
    if (!currentSelectedId) return undefined;
    return availableCriteriaOptions.find((item) => item.id === currentSelectedId);
  }, [availableCriteriaOptions, currentSelectedId]);

  const selectedOptionValue = useMemo(() => {
    if (testcase?.data?.is_custom) return OTHER_OPTION_VALUE;
    if (currentSelectedId) return String(currentSelectedId);
    return "";
  }, [testcase?.data?.is_custom, currentSelectedId]);

  const isCustom = testcase?.data?.is_custom ?? false;
  const isCatalogDerived = !!currentSelectedId && !isCustom;
  const isNewItem = testcase?.change === FormComponentChange.Added;
  const isUnsaved = isNewItem || isEditing || !testcase?.data?.id;
  const hasNoSelection = !isCustom && !currentSelectedId;
  const isSavedCustom = !isUnsaved && isCustom;
  const isSavedPredefined = !isUnsaved && isCatalogDerived;

  useEffect(() => {
    if (isNewItem) {
      setIsEditing(true);
    }
  }, [isNewItem]);

  const prev = useMemo(() => (isEditing ? testcase?.data : undefined), [isEditing, testcase?.data]);

  const { mutateAsync: createTestCase } = useFormAddPovTestCaseMutation(opportunityId);
  const { mutateAsync: updateTestCase } = useFormUpdatePovTestCaseMutation(opportunityId);
  const { mutateAsync: deleteTestCase } = useFormDeletePovTestCaseMutation(opportunityId);

  const handleInputChange = (field: string, value: string) => {
    updateFormItem(formSectionId, field, value);
    if (testcase?.error) {
      setFormError(formSectionId, "");
    }
  };

  const savePredefinedSelection = async (data: Record<string, any>) => {
    const usecaseId = parentUseCase?.data?.id;
    if (!usecaseId) {
      setIsEditing(false);
      return;
    }

    const payload = {
      ...data,
      pov_usecase: +usecaseId,
      product_feature_success_criteria: data.product_feature_success_criteria ?? null,
      is_custom: false,
    } as TestCase;

    setIsAutoSaving(true);
    try {
      let result;
      if (testcase?.change === FormComponentChange.Added) {
        result = await createTestCase(payload);
      } else {
        result = await updateTestCase(payload);
      }

      setFormItemSaved(formSectionId, {
        ...result,
        feature_name: data.feature_name,
        feature_id: data.feature_id,
      });
      setIsEditing(false);
      setFormError(formSectionId, undefined, undefined);
    } catch (error) {
      setFormError(formSectionId, "Failed to save test case", FormErrorType.Api);
      console.error("Error auto-saving predefined test case:", error);
    } finally {
      setIsAutoSaving(false);
    }
  };

  const handleCriteriaSelectionChange = async (value: string) => {
    if (value === OTHER_OPTION_VALUE) {
      updateFormItem(formSectionId, "is_custom", true);
      updateFormItem(formSectionId, "product_feature_success_criteria", null);
      updateFormItem(formSectionId, "title", "");
      updateFormItem(formSectionId, "description", "");
      updateFormItem(formSectionId, "feature_name", "");
      updateFormItem(formSectionId, "feature_id", null);

      if (testcase?.error) {
        setFormError(formSectionId, "");
      }

      setIsEditing(true);
      return;
    }

    const selectedId = Number(value);
    const selected = availableCriteriaOptions.find((item) => item.id === selectedId);
    if (!selected) return;

    const nextData = {
      ...testcase?.data,
      is_custom: false,
      product_feature_success_criteria: selected.id,
      title: selected.title,
      description: selected.description,
      feature_name: selected.feature_name,
      feature_id: selected.feature_id,
    };

    setFormItem(formSectionId, nextData);

    if (testcase?.error) {
      setFormError(formSectionId, "");
    }

    await savePredefinedSelection(nextData);
  };

  const handleDeleteTestcase = async () => {
    if (isUnsaved) {
      removeFormItem(formSectionId);
      return;
    }

    try {
      await deleteTestCase(testcase?.data as TestCase);
      removeFormItem(formSectionId);
    } catch (error) {
      setFormError(formSectionId, "Failed to delete test case", FormErrorType.Api);
      console.error("Error deleting test case:", error);
    }
  };

  const handleOpenDeleteConfirmModal = () => {
    openModal({
      content: () => (
        <StbModal
          open={true}
          role="delete"
          onSubmit={handleDeleteTestcase}
          onClose={async () => closeModal()}
        >
          <div>
            {isUnsaved
              ? "Discard this unsaved test case?"
              : "Are you sure you want to delete this test case?"}
          </div>
        </StbModal>
      ),
    });
  };

  const handleCancelClick = () => {
    if (isUnsaved && !isEditing) {
      removeFormItem(formSectionId);
    } else {
      setIsEditing(false);
      setFormItem(formSectionId, prev);
    }
    setFormError(formSectionId, undefined, undefined);
  };

  const handleSaveClick = async () => {
    const trimmedTitle = testcase?.data?.title?.trim() || "";
    const trimmedDescription = testcase?.data?.description?.trim() || "";

    if (!trimmedTitle) {
      setFormError(formSectionId, "Title is required", FormErrorType.Form);
      return;
    }

    try {
      const usecaseId = parentUseCase?.data?.id;
      const payload = {
        ...testcase?.data,
        title: trimmedTitle,
        description: trimmedDescription,
        pov_usecase: +usecaseId,
        product_feature_success_criteria: null,
        is_custom: true,
      } as TestCase;

      let result;
      if (testcase?.change === FormComponentChange.Added) {
        result = await createTestCase(payload);
      } else {
        result = await updateTestCase(payload);
      }

      setIsEditing(false);
      setFormItemSaved(formSectionId, {
        ...result,
        feature_name: testcase?.data?.feature_name,
        feature_id: testcase?.data?.feature_id,
      });
      setFormError(formSectionId, undefined, undefined);
    } catch (error) {
      setFormError(formSectionId, "Failed to save test case", FormErrorType.Api);
      console.error("Error saving custom test case:", error);
    }
  };

  const showSelector = isEditing || isNewItem;
  const showCustomInputs = showSelector && testcase?.data?.is_custom;

  const showCancelOnly = isUnsaved && hasNoSelection && !isAutoSaving;
  const showSaveCancel = isUnsaved && isCustom && !isAutoSaving;
  const showEditDelete = isSavedCustom && !isEditing && !isAutoSaving;
  const showDeleteOnly = (isSavedPredefined || (!isUnsaved && !isSavedCustom)) && !isAutoSaving;

  return (
    <div className="mb-4">
      <div className="flex flex-row justify-between w-full align-center">
        <div className="grow mr-4">
          {!showSelector || isCatalogDerived ? (
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <div className="text-sm font-semibold">{testcase?.data?.title}</div>
              {testcase?.data?.feature_name && (
                <HbrTag sentiment="info" size="small">
                  {testcase.data.feature_name}
                </HbrTag>
              )}
            </div>
          ) : (
            <>
              <HbrSelect
                value={selectedOptionValue}
                placeholder="Select success criteria"
                onHbr-change={(e) => {
                  handleCriteriaSelectionChange(asSingleValue(e.target.value));
                }}
              >
                <div slot="label">Test Case</div>
                {availableCriteriaOptions.map((item) => (
                  <HbrOption key={item.id} value={String(item.id)}>
                    {item.label}
                  </HbrOption>
                ))}
                <HbrOption value={OTHER_OPTION_VALUE}>Other</HbrOption>
              </HbrSelect>

              {showCustomInputs && (
                <div className="mt-3">
                  <HbrInput
                    value={testcase?.data?.title}
                    placeholder="Test case title"
                    className="grow"
                    maxlength={MAX_TITLE_LENGTH}
                    onHbr-input={(e) => handleInputChange("title", unicodeToPlain(e.target.value))}
                    error={testcase?.error?.length > 0}
                  >
                    <div slot="label">Name</div>
                    {testcase?.error && testcase?.errorType === FormErrorType.Form && (
                      <div slot="help-text" style={{ color: "var(--hbr-color-danger)" }}>
                        {testcase?.error}
                      </div>
                    )}
                  </HbrInput>
                </div>
              )}

              {!testcase?.data?.is_custom && selectedCriteriaOption?.description ? (
                <div className="text-xs text-gray-600 mt-2">{selectedCriteriaOption.description}</div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center justify-end">
          {showCancelOnly && (
            <StbButton
              iconOnly={true}
              role="cancel"
              size="small"
              variant="text"
              className="mr-1"
              onClick={() => handleCancelClick()}
            />
          )}

          {showSaveCancel && (
            <>
              <StbButton
                iconOnly={true}
                role="save"
                size="small"
                variant="text"
                className="mr-1"
                onClick={() => handleSaveClick()}
              />
              <StbButton
                iconOnly={true}
                role="cancel"
                size="small"
                variant="text"
                className="mr-1"
                onClick={() => handleCancelClick()}
              />
            </>
          )}

          {showEditDelete && (
            <>
              <StbButton
                iconOnly={true}
                role="update"
                size="small"
                variant="text"
                className="mr-1"
                onClick={() => setIsEditing(true)}
              />
              <StbButton
                iconOnly={true}
                role="delete"
                size="small"
                variant="text"
                onClick={() => handleOpenDeleteConfirmModal()}
              />
            </>
          )}

          {showDeleteOnly && (
            <StbButton
              iconOnly={true}
              role="delete"
              size="small"
              variant="text"
              onClick={() => handleOpenDeleteConfirmModal()}
            />
          )}
        </div>
      </div>

      <div className="text-sm font-normal mb-2">
        {showCustomInputs ? (
          <HbrTextarea
            className="w-full mt-3"
            value={testcase?.data?.description}
            placeholder="Add test case description"
            maxlength={MAX_DESCRIPTION_LENGTH}
            onHbr-input={(e) => handleInputChange("description", unicodeToPlain(e.target.value))}
          />
        ) : testcase?.data?.description?.length > 0 ? (
          <div>{testcase?.data?.description}</div>
        ) : (
          <div className="italic text-sm">No description</div>
        )}
      </div>

      {testcase?.error && testcase?.errorType === FormErrorType.Api && (
        <div style={{ color: "var(--hbr-color-danger)" }}>{testcase?.error}</div>
      )}
    </div>
  );
};

export default FormSectionTestCase;